import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { CategoryCard } from '../../components/admin/CategoryCard';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import {
  useGetAdminCategoriesQuery,
  useDeleteCategoryMutation,
  useToggleCategoryVisibilityMutation,
} from '../../store/api/adminApi';
import { useLanguage } from '../../hooks/useLanguage';
import { formatDate } from '../../utils/dateFormatter';

export function AdminCategoriesPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [includeHidden, setIncludeHidden] = useState(false);
  const [parentIdFilter, setParentIdFilter] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    categoryId: string | null;
    categoryName: string;
  }>({ isOpen: false, categoryId: null, categoryName: '' });
  
  const [visibilityDialog, setVisibilityDialog] = useState<{
    isOpen: boolean;
    categoryId: string | null;
    categoryName: string;
    currentVisibility: boolean;
  }>({ isOpen: false, categoryId: null, categoryName: '', currentVisibility: false });

  const { data, isLoading, error, refetch } = useGetAdminCategoriesQuery({
    includeHidden,
    parentId: parentIdFilter,
  });

  const [deleteCategory, { isLoading: isDeleting }] = useDeleteCategoryMutation();
  const [toggleVisibility, { isLoading: isToggling }] = useToggleCategoryVisibilityMutation();

  const handleDeleteClick = (categoryId: string, categoryName: string) => {
    setDeleteDialog({ isOpen: true, categoryId, categoryName });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.categoryId) return;

    try {
      await deleteCategory(deleteDialog.categoryId).unwrap();
      toast.success(t('admin.categories.deleteSuccess'));
      setDeleteDialog({ isOpen: false, categoryId: null, categoryName: '' });
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.categories.deleteError')
      );
    }
  };

  const handleToggleVisibilityClick = (categoryId: string, categoryName: string, currentVisibility: boolean) => {
    setVisibilityDialog({ isOpen: true, categoryId, categoryName, currentVisibility });
  };

  const handleToggleVisibilityConfirm = async () => {
    if (!visibilityDialog.categoryId) return;

    try {
      await toggleVisibility({
        categoryId: visibilityDialog.categoryId,
        isHidden: !visibilityDialog.currentVisibility,
      }).unwrap();
      toast.success(t('admin.categories.visibilitySuccess'));
      setVisibilityDialog({ isOpen: false, categoryId: null, categoryName: '', currentVisibility: false });
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.categories.visibilityError')
      );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-danger mb-4">
            {t('admin.categories.error.title')}
          </h2>
          <p className="text-neutral-medium font-body mb-6">
            {'data' in (error as { data?: { message?: string } })
              ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
              : t('errors.network')}
          </p>
          <button
            onClick={() => void refetch()}
            className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  const categories = data?.data.categories || [];

  // Filter categories by search term
  const filteredCategories = categories.filter((category) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      category.name.toLowerCase().includes(search) ||
      category.nameEn.toLowerCase().includes(search) ||
      category.description?.toLowerCase().includes(search) ||
      category.descriptionEn?.toLowerCase().includes(search)
    );
  });

  // Get parent categories for filter
  const parentCategories = categories.filter((cat) => cat.isParent);

  // Statistics
  const totalCategories = categories.length;
  const hiddenCategories = categories.filter((cat) => cat.isHidden).length;
  const parentCategoriesCount = categories.filter((cat) => cat.isParent).length;

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
            {t('admin.categories.title')}
          </h1>
          <p className="text-neutral-medium text-lg font-body">
            {t('admin.categories.subtitle')}
          </p>
        </div>
        <Link
          to="/admin/categories/new"
          className="px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
        >
          {t('admin.categories.createNew')}
        </Link>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card-glass rounded-lg p-4 border border-neutral-dark">
          <div className="text-sm text-neutral-medium font-body mb-1">
            {t('admin.categories.stats.total')}
          </div>
          <div className="text-2xl font-heading font-bold text-brand-accent">
            {totalCategories}
          </div>
        </div>
        <div className="card-glass rounded-lg p-4 border border-neutral-dark">
          <div className="text-sm text-neutral-medium font-body mb-1">
            {t('admin.categories.stats.hidden')}
          </div>
          <div className="text-2xl font-heading font-bold text-neutral-light">
            {hiddenCategories}
          </div>
        </div>
        <div className="card-glass rounded-lg p-4 border border-neutral-dark">
          <div className="text-sm text-neutral-medium font-body mb-1">
            {t('admin.categories.stats.parents')}
          </div>
          <div className="text-2xl font-heading font-bold text-neutral-light">
            {parentCategoriesCount}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-glass rounded-lg p-4 mb-6 border border-neutral-dark">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.categories.filters.search')}
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('admin.categories.filters.searchPlaceholder')}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            />
          </div>

          {/* Include Hidden */}
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeHidden}
                onChange={(e) => setIncludeHidden(e.target.checked)}
                className="w-4 h-4 text-brand-accent bg-neutral-dark border-neutral-dark rounded focus:ring-brand-accent"
              />
              <span className="text-sm text-neutral-medium font-body">
                {t('admin.categories.filters.includeHidden')}
              </span>
            </label>
          </div>

          {/* Parent Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.categories.filters.parent')}
            </label>
            <select
              value={parentIdFilter || ''}
              onChange={(e) => setParentIdFilter(e.target.value || undefined)}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="">{t('admin.categories.filters.all')}</option>
              {parentCategories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {currentLanguage === 'ar' ? cat.name : cat.nameEn}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Categories List */}
      {filteredCategories.length === 0 ? (
        <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
          <p className="text-neutral-medium text-lg font-body">
            {t('admin.categories.empty')}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="md:hidden space-y-4">
            {filteredCategories.map((category) => (
              <CategoryCard
                key={category._id}
                category={category}
                onToggleVisibility={handleToggleVisibilityClick}
                onDelete={handleDeleteClick}
                isToggling={isToggling}
                isDeleting={isDeleting}
              />
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block card-glass rounded-2xl shadow-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
              <thead className="bg-neutral-dark/50">
                <tr>
                  <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                    {t('admin.categories.table.name')}
                  </th>
                  <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                    {t('admin.categories.table.price')}
                  </th>
                  <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                    {t('admin.categories.table.products')}
                  </th>
                  <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                    {t('admin.categories.table.status')}
                  </th>
                  <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                    {t('admin.categories.table.type')}
                  </th>
                  <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                    {t('admin.categories.table.created')}
                  </th>
                  <th className="px-3 sm:px-6 py-4 text-center text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                    {t('admin.categories.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-dark" style={{ borderColor: 'rgba(168, 224, 224, 0.5)' }}>
                {filteredCategories.map((category) => {
                  const categoryName = currentLanguage === 'ar' ? category.name : category.nameEn;
                  return (
                    <tr key={category._id} className="hover:bg-neutral-dark/30 transition-colors">
                      <td className="px-3 sm:px-6 py-4">
                        <div>
                          <div className="font-heading font-semibold text-neutral-light">
                            {categoryName}
                          </div>
                          {category.description && (
                            <div className="text-sm text-neutral-medium font-body mt-1 line-clamp-1">
                              {currentLanguage === 'ar' ? category.description : category.descriptionEn}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span className="text-neutral-light font-body text-sm">
                          {category.price.toFixed(2)} USD
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span className="text-neutral-light font-body text-sm">
                          {category.availableProductsCount}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-body ${
                            category.isHidden
                              ? 'bg-danger/20 text-danger'
                              : 'bg-green-500/20 text-green-500'
                          }`}
                        >
                          {category.isHidden
                            ? t('admin.categories.status.hidden')
                            : t('admin.categories.status.visible')}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-body ${
                            category.isParent
                              ? 'bg-brand-accent/20 text-brand-accent'
                              : 'bg-neutral-dark text-neutral-medium'
                          }`}
                        >
                          {category.isParent
                            ? t('admin.categories.type.parent')
                            : t('admin.categories.type.child')}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm text-neutral-medium font-body">
                          {formatDate(category.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
                          <Link
                            to={`/admin/categories/${category._id}/edit`}
                            className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
                            title={t('admin.categories.actions.edit')}
                          >
                            {t('admin.categories.actions.edit')}
                          </Link>
                          <button
                            onClick={() => handleToggleVisibilityClick(category._id, categoryName, category.isHidden)}
                            disabled={isToggling}
                            className={`px-3 py-1 rounded-lg transition-colors text-sm font-body ${
                              category.isHidden
                                ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                                : 'bg-neutral-dark text-neutral-medium hover:bg-neutral-dark/80'
                            }`}
                            title={
                              category.isHidden
                                ? t('admin.categories.actions.show')
                                : t('admin.categories.actions.hide')
                            }
                          >
                            {category.isHidden
                              ? t('admin.categories.actions.show')
                              : t('admin.categories.actions.hide')}
                          </button>
                          <button
                            onClick={() => handleDeleteClick(category._id, categoryName)}
                            disabled={isDeleting}
                            className="px-3 py-1 bg-danger/20 text-danger rounded-lg hover:bg-danger/30 transition-colors text-sm font-body disabled:opacity-50"
                            title={t('admin.categories.actions.delete')}
                          >
                            {t('admin.categories.actions.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, categoryId: null, categoryName: '' })}
        onConfirm={handleDeleteConfirm}
        title={t('admin.categories.deleteTitle')}
        message={t('admin.categories.deleteConfirm', { name: deleteDialog.categoryName })}
        confirmText={t('admin.categories.actions.delete')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* Visibility Toggle Confirmation Dialog */}
      <ConfirmDialog
        isOpen={visibilityDialog.isOpen}
        onClose={() => setVisibilityDialog({ isOpen: false, categoryId: null, categoryName: '', currentVisibility: false })}
        onConfirm={handleToggleVisibilityConfirm}
        title={
          visibilityDialog.currentVisibility
            ? t('admin.categories.showTitle')
            : t('admin.categories.hideTitle')
        }
        message={
          visibilityDialog.currentVisibility
            ? t('admin.categories.showConfirm', { name: visibilityDialog.categoryName })
            : t('admin.categories.hideConfirm', { name: visibilityDialog.categoryName })
        }
        confirmText={
          visibilityDialog.currentVisibility
            ? t('admin.categories.actions.show')
            : t('admin.categories.actions.hide')
        }
        type="info"
        isLoading={isToggling}
      />
    </div>
  );
}

